import { db } from '../firebase';
import { collection, query, where, getDocs, doc, writeBatch, serverTimestamp, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { MasterTransaction, LaborRecord } from '../types';
import { CashTip, getCashTipsByEmail } from './cashTipService';
import { JarTip } from './jarTipService';
import { getSettings, getSettingsAdmin } from './settingsService';
import { getStaff } from './staffService';
import { getRolePercentages } from './roleService';
import { getLaborRecords } from './laborService';
import { isManagerialRole } from '../constants';
import type { Firestore } from 'firebase-admin/firestore';

const normalizeShiftName = (shift?: string | null): string => {
  if (!shift || shift.trim() === '') return 'general';
  return shift.trim().toLowerCase();
};

export const getMasterTransactionById = async (tenantId: string, txId: string): Promise<MasterTransaction | null> => {
  const docRef = doc(db, 'tenants', tenantId, 'master_transactions', txId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() } as MasterTransaction;
  }
  return null;
};

// Dependency Check: Ensure POS labels (Reported tips) exist for the dates being allocated
export const verifyPOSImport = async (tenantId: string, contexts: { date: string, shift?: string }[]): Promise<{ date: string; shift?: string; hasPOS: boolean }[]> => {
  const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const results = [];
  
  // Create a cache of available POS contexts for the tenant to minimize repetitive queries
  const uniqueDates = Array.from(new Set(contexts.map(c => c.date)));
  const posCache: Record<string, Set<string>> = {}; // date -> Set of shiftNames

  for (const date of uniqueDates) {
    console.log(`ALLOCATION_DEBUG: Querying POS tips for date: ${date}, masterRef: ${masterRef.path}`);
    const q = query(masterRef, 
      where('tipDate', '==', date), 
      where('type', 'in', ['Reported', 'Submitted']), 
      where('source', 'in', ['POS', 'POS Override', 'Cash'])
    );
    const snap = await getDocs(q);
    console.log(`ALLOCATION_DEBUG: Found ${snap.docs.length} POS tips for date ${date} with query {type: in['Reported', 'Submitted'], source: in['POS', 'POS Override']}`);
    if (snap.docs.length === 0) {
      // Debug: see what's actually there
      const allDocsForDateQ = query(masterRef, where('tipDate', '==', date));
      const allDocsForDateSnap = await getDocs(allDocsForDateQ);
      console.log(`ALLOCATION_DEBUG: DIAGNOSTIC: Found ${allDocsForDateSnap.docs.length} total transactions for date ${date}`);
      allDocsForDateSnap.docs.forEach((d, i) => {
        const data = d.data();
        const typeMatch = ['Reported', 'Submitted'].includes(data.type);
        const sourceMatch = ['POS', 'POS Override'].includes(data.source);
        console.log(`ALLOCATION_DEBUG: DIAGNOSTIC Trans ${i}:`, {
          id: d.id,
          type: data.type,
          source: data.source,
          typeMatch,
          sourceMatch
        });
        if (!sourceMatch) {
          console.warn(`ALLOCATION_DEBUG: Mismatch on source for Trans ${i}: "${data.source}"`);
        }
      });
    }
    posCache[date] = new Set(snap.docs.map(doc => normalizeShiftName(doc.data().shiftName)));
    console.log(`ALLOCATION_DEBUG: POS cache for ${date}:`, posCache[date]);
  }
  
  for (const ctx of contexts) {
    const dateHasPOS = posCache[ctx.date] && posCache[ctx.date].size > 0;
    const shiftMatches = ctx.shift ? posCache[ctx.date]?.has(normalizeShiftName(ctx.shift)) : true;
    console.log(`ALLOCATION_DEBUG: Checking context`, ctx, `hasPOS: ${dateHasPOS}, matches: ${shiftMatches}`);
    results.push({ ...ctx, hasPOS: !!(dateHasPOS && shiftMatches) });
  }
  
  return results;
};

export interface UnifiedTip {
  id?: string;
  source_id?: string;
  staffId?: string;
  amount: number;
  tipDate: string;
  staffName: string;
  source: 'Cash' | 'Jar' | 'Calculated' | 'POS';
  email?: string;
  shift?: string;
  status?: string;
  type?: 'Submitted' | 'Reported' | 'Allocated';
  paymentStatus?: 'Paid' | 'Unpaid';
  submittedBy?: string;
  masterTxId?: string;
  payoutMethod?: string;
  payrollIncluded?: boolean;
  posId?: string;
  locationId?: string;
  locationName?: string;
}

export const saveAllocationsToDb = async (tenantId: string, results: UnifiedTip[], sourceTips: UnifiedTip[]) => {
  console.log('ALLOCATION_DEBUG: Finalizing allocations. Results:', results.length, ' Sources:', sourceTips.length, 'TenantId:', tenantId);
  
  const settings = await getSettings(tenantId);
  const allocationMethod = settings?.allocationMethod || 'Unknown';

  const MAX_BATCH_SIZE = 450;
  let currentBatch = writeBatch(db);
  let operationCount = 0;

  const commitAndReset = async () => {
    if (operationCount > 0) {
      try {
        console.log('ALLOCATION_DEBUG: Committing batch chunk for tenant:', tenantId);
        await currentBatch.commit();
        console.log('ALLOCATION_DEBUG: Batch chunk committed successfully');
      } catch (error) {
        console.error('ALLOCATION_DEBUG: Failed batch commit for tenant:', tenantId, error);
        handleFirestoreError(error, OperationType.WRITE, `allocation_batch_chunk/tenant=${tenantId}`);
      }
      currentBatch = writeBatch(db);
      operationCount = 0;
    }
  };

  // 1. Mark all SOURCE tips as Allocated in their respective collections
  for (const source of sourceTips) {
    if (source.id) {
      // If it's a Calculated adjustment, it's already in master_transactions, don't look up in tips
      if (source.source === 'Calculated') {
        // Proceed to process master transaction finalization only
      } else {
        const collectionName = source.source === 'Cash' ? 'cash_tips' : 'jar_tips';
        const tipRef = doc(db, 'tenants', tenantId, collectionName, source.id);
        
        console.log(`ALLOCATION_DEBUG: Verifying tip source ${collectionName}/${source.id} before batch update`);
        const tipSnap = await getDoc(tipRef);
        if(!tipSnap.exists()){
          console.error(`ALLOCATION_DEBUG: Source tip does not exist: ${collectionName}/${source.id}`);
          // Skip this operation instead of failing the batch
          continue;
        }
        
        currentBatch.set(tipRef, {
          status: 'Allocated',
          allocatedAmount: source.amount
        }, { merge: true });
        operationCount++;
      }

      // Also ensure the Master Transaction for this source is marked Finalized
      if (source.masterTxId) {
        const masterParentRef = doc(db, 'tenants', tenantId, 'master_transactions', source.masterTxId);
        
        let parentReportingStatus: any = 'reported';
        const sourceStr = (source.source || '').toLowerCase();
        if (sourceStr.includes('cash')) parentReportingStatus = 'cash_allocated';
        else if (sourceStr.includes('jar')) parentReportingStatus = 'jar_allocated';
        else if (sourceStr.includes('pos')) parentReportingStatus = 'pos_allocated';
        else if (sourceStr.includes('calculated')) parentReportingStatus = 'dispute_allocated';
        
        console.log(`ALLOCATION_DEBUG: Finalizing master transaction ${source.masterTxId}, source: ${source.id}`);
        currentBatch.set(masterParentRef, {
          allocationStatus: 'Finalized',
          submissionStatus: 'Finalized',
          reportingStatus: parentReportingStatus,
          source_id: source.source_id || source.id,
          auditVerified: true,
          auditActor: getAuth().currentUser?.email || 'admin'
        }, { merge: true });
        operationCount++;
      }

      if (operationCount >= MAX_BATCH_SIZE) await commitAndReset();
    }
  }

  // 2. Create NEW 'Allocated' transaction (the Child) for every payout result
  for (const result of results) {
    const parentTip = sourceTips.find(st => st.masterTxId === result.masterTxId);
    const parentSource = parentTip ? parentTip.source : result.source;
    
    let childReportingStatus: any = 'reported';
    const parentSourceStr = (parentSource || '').toLowerCase();
    if (parentSourceStr.includes('cash')) childReportingStatus = 'cash_allocated';
    else if (parentSourceStr.includes('jar')) childReportingStatus = 'jar_allocated';
    else if (parentSourceStr.includes('pos')) childReportingStatus = 'pos_allocated';
    else if (parentSourceStr.includes('calculated')) childReportingStatus = 'dispute_allocated';

    const childTxRef = doc(collection(db, 'tenants', tenantId, 'master_transactions'));
    currentBatch.set(childTxRef, {
      id: childTxRef.id,
      tenantId,
      timestamp: serverTimestamp(),
      staffId: result.staffId || null,
      staffName: result.staffName,
      email: result.email || 'N/A',
      tipDate: result.tipDate,
      amount: result.amount, 
      type: 'Allocated',
      reportingStatus: childReportingStatus,
      source: result.source === 'Calculated' ? 'SplitPool' : result.source,
      payoutMethod: result.payoutMethod || 'Paycheck',
      parentTxId: result.masterTxId || null, 
      source_id: result.source_id || null,
      posId: result.posId || null,
      shift: result.shift || null,
      shiftName: result.shift || null,
      allocationStatus: 'Finalized',
      submissionStatus: 'Finalized',
      payrollIncluded: false,
      analyticsIncluded: true,
      auditVerified: true,
      auditActor: getAuth().currentUser?.email || 'admin',
      allocationMethod,
      locationId: result.locationId || parentTip?.locationId || 'loc-downtown',
      locationName: result.locationName || parentTip?.locationName || 'Downtown Bistro'
    });
    operationCount++;

    // 3. Create allocation audit record
    const allocationAuditRef = doc(collection(db, 'tenants', tenantId, 'allocations'));
    currentBatch.set(allocationAuditRef, {
      tenantId,
      masterTxId: result.masterTxId || childTxRef.id, 
      childTxId: childTxRef.id, 
      staffName: result.staffName,
      amount: result.amount,
      tipDate: result.tipDate,
      createdAt: serverTimestamp(),
      locationId: result.locationId || parentTip?.locationId || 'loc-downtown',
      locationName: result.locationName || parentTip?.locationName || 'Downtown Bistro'
    });
    operationCount++;

    if (operationCount >= MAX_BATCH_SIZE) await commitAndReset();
  }
  
  await commitAndReset();
  console.log('ALLOCATION_DEBUG: Finalization complete');
};

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const auth = getAuth();
  const errInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export const getPendingAllocations = async (tenantId: string): Promise<UnifiedTip[]> => {
  const settings = await getSettings(tenantId);
  const cashTipsRef = collection(db, 'tenants', tenantId, 'cash_tips');
  const jarTipsRef = collection(db, 'tenants', tenantId, 'jar_tips');

  const cashQ = query(cashTipsRef, where('status', '==', 'Approved'));
  const jarQ = query(jarTipsRef, where('status', '==', 'Approved'));

  try {
    const [cashSnapshot, jarSnapshot] = await Promise.all([getDocs(cashQ), getDocs(jarQ)]);

    const cashTips = cashSnapshot.docs.map(doc => {
      const data = doc.data() as CashTip;
      console.log('DEBUG [getPendingAllocations] Fetching cash_tip:', doc.id, 'amount:', data.amount);
      return {
        id: doc.id,
        source_id: doc.id,
        staffId: data.staffId,
        amount: data.amount,
        tipDate: data.tipDate,
        staffName: data.staffName,
        email: data.email,
        shift: (data as any).shift || (data as any).shiftName || '',
        source: data.source || 'Cash',
        masterTxId: data.masterTxId,
        payoutMethod: settings?.payoutMethod || 'Paycheck',
        type: 'Submitted',
        posId: data.posId,
        locationId: data.locationId || 'loc-downtown',
        locationName: data.locationName || 'Downtown Bistro'
      } as UnifiedTip;
    });

    const jarTips = jarSnapshot.docs.map(doc => {
      const data = doc.data() as JarTip;
      return {
        id: doc.id,
        source_id: doc.id,
        staffId: data.staffId,
        amount: data.jarAmount,
        tipDate: data.tipDate,
        staffName: data.submitterName,
        shift: data.shift,
        source: 'Jar',
        masterTxId: data.masterTxId,
        payoutMethod: settings?.payoutMethod || 'Paycheck',
        type: 'Submitted',
        posId: data.posId,
        locationId: data.locationId || 'loc-downtown',
        locationName: data.locationName || 'Downtown Bistro'
      } as UnifiedTip;
    });

    return [...cashTips, ...jarTips];
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'cash_tips/jar_tips');
    return [];
  }
};

export const getPendingAllocationsAdmin = async (tenantId: string, dbInstance: Firestore): Promise<UnifiedTip[]> => {
  try {
    const settings = await getSettingsAdmin(tenantId, dbInstance);
    const cashTipsRef = dbInstance.collection('tenants').doc(tenantId).collection('cash_tips');
    const jarTipsRef = dbInstance.collection('tenants').doc(tenantId).collection('jar_tips');

    const cashQ = cashTipsRef.where('status', '==', 'Approved');
    const jarQ = jarTipsRef.where('status', '==', 'Approved');

    const [cashSnapshot, jarSnapshot] = await Promise.all([cashQ.get(), jarQ.get()]);

    const cashTips = cashSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        source_id: doc.id,
        staffId: data.staffId,
        amount: data.amount,
        tipDate: data.tipDate,
        staffName: data.staffName,
        email: data.email,
        shift: data.shift || data.shiftName || '',
        source: data.source || 'Cash',
        masterTxId: data.masterTxId,
        payoutMethod: settings?.payoutMethod || 'Paycheck',
        type: 'Submitted',
        posId: data.posId,
        locationId: data.locationId || 'loc-downtown',
        locationName: data.locationName || 'Downtown Bistro'
      } as UnifiedTip;
    });

    const jarTips = jarSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        source_id: doc.id,
        staffId: data.staffId,
        amount: data.jarAmount,
        tipDate: data.tipDate,
        staffName: data.submitterName,
        shift: data.shift,
        source: 'Jar',
        masterTxId: data.masterTxId,
        payoutMethod: settings?.payoutMethod || 'Paycheck',
        type: 'Submitted',
        posId: data.posId,
        locationId: data.locationId || 'loc-downtown',
        locationName: data.locationName || 'Downtown Bistro'
      } as UnifiedTip;
    });

    return [...cashTips, ...jarTips];
  } catch (error: any) {
    if (error?.code === 7 || error?.message?.includes('PERMISSION_DENIED')) {
      console.warn(`[getPendingAllocationsAdmin] Admin Firestore query not authorized for tenant "${tenantId}". Skipping background check.`);
    } else {
      console.error('Error in getPendingAllocationsAdmin:', error);
    }
    return [];
  }
};

export const getMasterTransactions = async (tenantId: string): Promise<any[]> => {
  const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
  try {
    const snapshot = await getDocs(masterRef);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'master_transactions');
    return [];
  }
};

export const calculateAllocations = async (tenantId: string, tips: UnifiedTip[]): Promise<UnifiedTip[]> => {
  console.log('CALC_DEBUG: Starting calculation for', tips.length, 'tips');
  if (tips.length === 0) return [];
  
  const settings = await getSettings(tenantId);
  const staff = await getStaff(tenantId);
  const rolePercentages = await getRolePercentages(tenantId);

  console.log('CALC_DEBUG: Settings:', settings);
  console.log('CALC_DEBUG: Staff count:', staff.length);
  console.log('CALC_DEBUG: RolePercentages:', rolePercentages);

  if (!settings || !settings.allocationMethod) {
    throw new Error('Allocation method is not configured. Please check your settings.');
  }

  let method = settings.allocationMethod.trim();
  if (method.toLowerCase() === 'role') {
    method = 'Split by Role';
  } else if (method.toLowerCase() === 'equal') {
    method = 'Equal split';
  }
  const isShiftBased = method.toLowerCase().includes('by shift');
  console.log('CALC_DEBUG: Method:', method, 'isShiftBased:', isShiftBased);

  const results: UnifiedTip[] = [];
  let groupTotalInput = 0;

  // Group tips based on Method (Daily vs Shift)
  const groups: Record<string, UnifiedTip[]> = {};
  tips.forEach(tip => {
    const key = isShiftBased 
      ? `${tip.tipDate}_${tip.shift || 'General'}_${tip.payoutMethod || 'Paycheck'}` 
      : `${tip.tipDate}_${tip.payoutMethod || 'Paycheck'}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(tip);
    console.log('CALC_DEBUG: Adding tip to group', key, tip.amount);
  });

  for (const key in groups) {
    const parts = key.split('_');
    const date = parts[0];
    const shiftFilter = isShiftBased ? (parts[1] === 'General' ? undefined : parts[1]) : undefined;
    
    const groupTips = groups[key];
    const totalGroupAmount = groupTips.reduce((sum, t) => sum + t.amount, 0);
    groupTotalInput += totalGroupAmount;
    if (totalGroupAmount === 0) continue;

    console.log(`CALC_DEBUG: Processing Group key: ${key}, method: ${method}, totalGroupAmount: ${totalGroupAmount}`);
    
    // Build Labor Map for this context (staffId -> hours)
    const laborMap: Record<string, number> = {};
    
    // a. POS Labor (max hours per staff in this shift/date to avoid dupes)
    if (settings.usePosShiftDataForActuals !== false) {
      const posQuery = query(collection(db, 'tenants', tenantId, 'master_transactions'), 
        where('tipDate', '==', date), 
        where('type', 'in', ['Reported', 'Submitted', 'Allocated']),
        where('source', 'in', ['POS', 'POS Override', 'Cash'])
      );
      const posSnap = await getDocs(posQuery);
      console.log("CALC_DEBUG: POS tips for labor for group:", key, posSnap.docs.length, 'docs');
      if (posSnap.empty) {
        console.warn(`POS data for ${date} ${isShiftBased ? `(shift: ${shiftFilter || 'General'})` : ''} is required but missing.`);
        continue;
      }
      const calculateHours = (start: string, end: string): number => {
        if (!start || !end) return 0;
        try {
          const [sH, sM] = start.split(':').map(Number);
          const [eH, eM] = end.split(':').map(Number);
          let startMin = sH * 60 + sM;
          let endMin = eH * 60 + eM;
          if (endMin <= startMin) endMin += 24 * 60;
          return Number(((endMin - startMin) / 60).toFixed(2));
        } catch (e) { return 0; }
      };

      posSnap.docs.forEach(doc => {
        const d = doc.data();
        if (!d.staffId) return;
        if (isShiftBased && normalizeShiftName(d.shiftName) !== normalizeShiftName(shiftFilter)) return;
        
        let hours = 0;
        if (settings.usePosShiftDataForActuals === true && d.shiftStartTime && d.shiftEndTime) {
           hours = calculateHours(d.shiftStartTime, d.shiftEndTime);
        } else {
           hours = d.hoursWorked || 0;
        }
        
        laborMap[d.staffId] = Math.max(laborMap[d.staffId] || 0, hours);
      });
    }

    // b. Manual Labor
    const manualRecords = await getLaborRecords(tenantId, date, shiftFilter);
    manualRecords.forEach(r => {
      laborMap[r.staffId] = Math.max(laborMap[r.staffId] || 0, r.hoursWorked);
    });

    const eligibleRoles = rolePercentages.filter(rp => rp.isEligible !== false).map(rp => rp.role);
    let groupStaff = staff.filter(s => eligibleRoles.includes(s.role));
    
    // Filter to staff who actually have hours if any hours exist for the group
    const hasAnyLabor = Object.keys(laborMap).length > 0;
    console.log("CALC_DEBUG: LaborMap for group:", laborMap, "Staff in group:", groupStaff.map(s => s.id));
    if (hasAnyLabor) {
      const staffWithHours = groupStaff.filter(s => {
        const hasHours = laborMap[s.id!] !== undefined;
        console.log("CALC_DEBUG: Staff", s.name, s.id, "hasHours:", hasHours);
        return hasHours;
      });
      if (staffWithHours.length > 0) {
        groupStaff = staffWithHours;
      } else {
        console.log("CALC_DEBUG: Labor data exists but no eligible staff have recorded hours. Falling back to all eligible staff.");
      }
    }

    // Smart protection: If multiple employees are working on the shift, exclude managers/owners from the shared pool.
    // (If an owner or manager is working a solo shift with no other staff, they retain 100% of their earnings).
    const nonManagerStaff = groupStaff.filter(s => !isManagerialRole(s.role));
    if (nonManagerStaff.length > 0) {
      groupStaff = nonManagerStaff;
    }

    if (groupStaff.length === 0) {
      const hasPooledTip = groupTips.some(t => t.source === 'Jar' || !t.staffId || t.staffId === 'unknown');
      if (hasPooledTip) {
        throw new Error(`Cannot approve allocation: No active employees in eligible roles (e.g., Server, Bartender) were found for ${date}${shiftFilter ? ` shift ${shiftFilter}` : ''}. Please add at least one eligible staff member in Staff Management first.`);
      }
      results.push(...groupTips);
      continue;
    }

    if (method.toLowerCase().includes('equal')) {
      // EQUAL SPLIT (Hourly Pro-rated)
      const totalHours = groupStaff.reduce((sum, s) => sum + (laborMap[s.id!] || (hasAnyLabor ? 0 : 1)), 0);
      const totalGroupAmountCents = Math.round(totalGroupAmount * 100);
      let allocatedCents = 0;

      groupStaff.forEach((s, index) => {
        const staffHours = laborMap[s.id!] || (hasAnyLabor ? 0 : 1);
        if (staffHours === 0 && hasAnyLabor) return;

        let shareCents = totalHours > 0 
          ? Math.round(totalGroupAmountCents * (staffHours / totalHours))
          : Math.round(totalGroupAmountCents / groupStaff.length);
          
        if (index === groupStaff.length - 1) shareCents = totalGroupAmountCents - allocatedCents;
        allocatedCents += shareCents;
        const shareDollars = shareCents / 100;

        if (shareDollars !== 0) {
          results.push({
            staffId: s.id,
            amount: shareDollars,
            tipDate: date,
            staffName: s.name,
            email: s.email,
            source: 'Calculated',
            masterTxId: groupTips[0].masterTxId,
            shift: shiftFilter || groupTips[0].shift,
            payoutMethod: groupTips[0].payoutMethod || 'Paycheck',
            posId: s.posId || groupTips[0].posId,
            locationId: groupTips[0].locationId || s.locationId || 'loc-downtown',
            locationName: groupTips[0].locationName || s.locationName || 'Downtown Bistro'
          });
        }
      });
    } else if (method.toLowerCase().includes('split by role')) {
      // ROLE BASED (Hourly Pro-rated within role)
      
      // Filter roles to those that actually have staff working
      const activeRoles = rolePercentages.filter(rp => {
        if (rp.isEligible === false || rp.percentage <= 0) return false;
        return groupStaff.some(s => s.role === rp.role);
      });

      if (activeRoles.length === 0) {
        // Fallback: if no defined roles are occupied, split equally among all present staff
        const totalHours = groupStaff.reduce((sum, s) => sum + (laborMap[s.id!] || (hasAnyLabor ? 0 : 1)), 0);
        groupStaff.forEach(s => {
          const staffHours = laborMap[s.id!] || (hasAnyLabor ? 0 : 1);
          if (staffHours === 0 && hasAnyLabor) return;
          const share = totalHours > 0 ? (totalGroupAmount * (staffHours / totalHours)) : (totalGroupAmount / groupStaff.length);
          if (share > 0) {
            results.push({
              staffId: s.id,
              amount: Number(share.toFixed(2)),
              tipDate: date,
              staffName: s.name,
              email: s.email,
              source: 'Calculated',
              masterTxId: groupTips[0].masterTxId,
              shift: shiftFilter || groupTips[0].shift,
              payoutMethod: groupTips[0].payoutMethod || 'Paycheck',
              posId: s.posId || groupTips[0].posId,
              locationId: groupTips[0].locationId || s.locationId || 'loc-downtown',
              locationName: groupTips[0].locationName || s.locationName || 'Downtown Bistro'
            });
          }
        });
        continue;
      }

       // Normalize percentages of active roles to sum to 100%
      const totalActivePercentage = activeRoles.reduce((sum, rp) => sum + rp.percentage, 0);

      activeRoles.forEach(rp => {
        const normalizedPercentage = (rp.percentage / totalActivePercentage) * 100;
        const rolePoolCents = Math.round((totalGroupAmount * (normalizedPercentage / 100)) * 100);
        const staffInRole = groupStaff.filter(s => s.role === rp.role);
        const totalRoleHours = staffInRole.reduce((sum, s) => sum + (laborMap[s.id!] || (hasAnyLabor ? 0 : 1)), 0);
        
        let allocatedRoleCents = 0;

        staffInRole.forEach((s, index) => {
          const staffHours = laborMap[s.id!] || (hasAnyLabor ? 0 : 1);
          if (staffHours === 0 && hasAnyLabor) return;

          let shareCents = totalRoleHours > 0
            ? Math.round(rolePoolCents * (staffHours / totalRoleHours))
            : Math.round(rolePoolCents / staffInRole.length);
          
          if (index === staffInRole.length - 1) shareCents = rolePoolCents - allocatedRoleCents;
          allocatedRoleCents += shareCents;
          const shareDollars = shareCents / 100;

          if (shareDollars > 0) {
            results.push({
              staffId: s.id,
              amount: shareDollars,
              tipDate: date,
              staffName: s.name,
              email: s.email,
              source: 'Calculated',
              masterTxId: groupTips[0].masterTxId,
              shift: shiftFilter || groupTips[0].shift,
              payoutMethod: groupTips[0].payoutMethod || 'Paycheck',
              posId: s.posId || groupTips[0].posId,
              locationId: groupTips[0].locationId || s.locationId || 'loc-downtown',
              locationName: groupTips[0].locationName || s.locationName || 'Downtown Bistro'
            });
          }
        });
      });
    } else if (method === 'Keep Individual Tips') {
      results.push(...groupTips);
    } else {
      throw new Error(`Unrecognized allocation method: "${method}"`);
    }
  }

  const groupTotalOutput = results.reduce((sum, r) => sum + r.amount, 0);
  console.log(`CALC_DEBUG: Finished. Input: ${groupTotalInput}, Output: ${groupTotalOutput.toFixed(2)}, Count: ${results.length}`);
  return results;
};

export const getPOSReportedTotals = async (tenantId: string, dates: string[]): Promise<Record<string, number>> => {
  if (dates.length === 0) return {};
  
  const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const q = query(masterRef, where('type', '==', 'Reported'), where('source', '==', 'POS'));
  
  try {
    const snapshot = await getDocs(q);
    const totals: Record<string, number> = {};
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      if (dates.includes(data.tipDate)) {
        totals[data.tipDate] = (totals[data.tipDate] || 0) + (data.amount || 0);
      }
    });
    
    return totals;
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'master_transactions');
    return {};
  }
};

export const getAllocationDetails = async (tenantId: string, staffName: string, date: string): Promise<UnifiedTip[]> => {
  const cashTipsRef = collection(db, 'tenants', tenantId, 'cash_tips');
  const jarTipsRef = collection(db, 'tenants', tenantId, 'jar_tips');

  const cashQ = query(cashTipsRef, where('staffName', '==', staffName), where('tipDate', '==', date));
  const jarQ = query(jarTipsRef, where('staffName', '==', staffName), where('tipDate', '==', date));

  try {
    const [cashSnapshot, jarSnapshot] = await Promise.all([getDocs(cashQ), getDocs(jarQ)]);

    const cashTips = cashSnapshot.docs.map(doc => {
      const data = doc.data() as CashTip;
      return {
        id: doc.id,
        amount: data.amount,
        tipDate: data.tipDate,
        staffName: data.staffName,
        shift: (data as any).shift,
        source: 'Cash'
      } as UnifiedTip;
    });

    const jarTips = jarSnapshot.docs.map(doc => {
      const data = doc.data() as JarTip;
      return {
        id: doc.id,
        amount: data.jarAmount,
        tipDate: data.tipDate,
        staffName: data.submitterName,
        shift: data.shift,
        source: 'Jar'
      } as UnifiedTip;
    });

    return [...cashTips, ...jarTips];
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'cash_tips/jar_tips');
    return [];
  }
};

export const getMasterTransactionsByEmail = async (tenantId: string, email: string): Promise<MasterTransaction[]> => {
  const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const q = query(masterRef, where('email', '==', email));
  try {
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'master_transactions');
    return [];
  }
};

export const getMasterTransactionsByStaffId = async (tenantId: string, staffId: string): Promise<MasterTransaction[]> => {
  const masterRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const q = query(masterRef, where('staffId', '==', staffId));
  try {
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));
  } catch (error) {
    handleFirestoreError(error, OperationType.GET, 'master_transactions');
    return [];
  }
};

export interface AllocationAudit {
  id: string;
  staffName: string;
  amount: number;
  tipDate: string;
  createdAt: any; 
  masterTxId: string;
  childTxId: string;
  locationId?: string;
  locationName?: string;
}

export const getAllocationsHistory = async (tenantId: string): Promise<AllocationAudit[]> => {
  const allocRef = collection(db, 'tenants', tenantId, 'allocations');
  const snapshot = await getDocs(allocRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as AllocationAudit));
};

export const approveMasterTransactionByEmployee = async (
  tenantId: string,
  txId: string,
  signatureType: 'type' | 'draw',
  signatureText?: string,
  signatureDrawn?: string
) => {
  console.log(`DEBUG: Approving TX ${txId} in Firestore...`);
  const docRef = doc(db, 'tenants', tenantId, 'master_transactions', txId);
  await updateDoc(docRef, {
    employeeApproved: true,
    employeeApprovalTimestamp: serverTimestamp(),
    employeeApprovalSignatureType: signatureType,
    employeeApprovalSignatureText: signatureText || null,
    employeeApprovalSignatureDrawn: signatureDrawn || null
  });
  console.log(`DEBUG: TX ${txId} approved successfully in Firestore.`);
};
